<script setup lang="ts">
import { computed, h, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import AppDialog from '@/components/ui/Dialog.vue'
import DateField from '@/components/ui/DateField.vue'
import DenseTable from '@/components/ui/DenseTable.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Paginator from 'primevue/paginator'
import Select from 'primevue/select'
import Tag from 'primevue/tag'
import { useAuthStore } from '@/stores/auth'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useFiltrosEnUrl } from '@/composables/useFiltrosEnUrl'
import { formatCurrency, formatShortDate } from '@/utils/format'
import {
  STORE_SALE_PRICE_TYPES,
  storeSalesApi,
  type Advisor,
  type StoreSale,
  type StoreSalePriceType,
} from '@/services/storeSales'

/**
 * Listado de ventas de tienda.
 *
 * Se parece al de cotizaciones en los filtros y en las acciones, pero no
 * comparte su tabla: una venta no tiene artículos ni estado logístico. En
 * escritorio se usa `DenseTable`; por debajo de 1024 px se pinta una tarjeta por
 * venta con las mismas acciones, porque una acción que solo existe en la tabla
 * no existe en móvil.
 *
 * Los filtros viven en la URL por la misma razón que en cotizaciones: abrir una
 * venta y volver no puede dejar la lista entera.
 */

const router = useRouter()
const authStore = useAuthStore()
const layout = useBreakpoint()

const isDesktop = computed(() => layout.value === 'desktop')
const canManage = computed(() => authStore.hasPermission('store-sales.manage'))

const items = ref<StoreSale[]>([])
const advisors = ref<Advisor[]>([])
const loading = ref(false)
const errorMessage = ref<string | null>(null)
const total = ref(0)
const perPage = 20

const search = ref('')
const dateFrom = ref('')
const dateTo = ref('')
const priceType = ref('')
const invoiced = ref('')
const advisorId = ref<string | number | null>('')
const page = ref(1)

const priceTypeOptions = [
  { label: 'Todos', value: '' },
  ...STORE_SALE_PRICE_TYPES,
]

const filtros = useFiltrosEnUrl({
  search: { ref: search, texto: true },
  date_from: { ref: dateFrom, fecha: true },
  date_to: { ref: dateTo, fecha: true },
  price_type: { ref: priceType, permitidos: ['retail', 'wholesale'] },
  is_invoiced: { ref: invoiced, permitidos: ['1', '0'] },
  user_id: { ref: advisorId, entero: true },
  page: { ref: page, numero: true },
})

interface SaleRow extends StoreSale {
  sale_date_label: string
  price_type_label: string
  user_name_label: string
  amount_label: string
  invoice_state: 'FACTURADA' | 'PENDIENTE'
  invoice_number_label: string
  customer_email: string | null
  actions: Array<{ label: string; value: string; icon: string; severity?: string }>
}

function acciones(venta: StoreSale): SaleRow['actions'] {
  if (!canManage.value) return [{ label: 'Ver detalle', value: 'view', icon: 'pi pi-eye' }]

  const acciones: SaleRow['actions'] = [
    { label: 'Ver detalle', value: 'view', icon: 'pi pi-eye' },
    { label: 'Editar', value: 'edit', icon: 'pi pi-pencil' },
  ]

  if (!venta.is_invoiced) {
    acciones.push({ label: 'Marcar como facturada', value: 'invoice', icon: 'pi pi-check-circle' })
  }

  acciones.push({ label: 'Borrar', value: 'delete', icon: 'pi pi-trash', severity: 'danger' })

  return acciones
}

const rows = computed<SaleRow[]>(() =>
  items.value.map((venta) => ({
    ...venta,
    sale_date_label: formatShortDate(venta.sale_date),
    price_type_label: venta.price_type === 'retail' ? 'Detal' : 'Mayoreo',
    user_name_label: venta.user_name ?? '—',
    amount_label: formatCurrency(venta.amount),
    invoice_state: venta.is_invoiced ? 'FACTURADA' : 'PENDIENTE',
    invoice_number_label: venta.invoice_number ?? '—',
    actions: acciones(venta),
  })),
)

/**
 * Dos líneas en la celda del cliente: nombre y correo.
 *
 * Se devuelven etiquetas sueltas y no un componente: `DenseTable` invoca la
 * plantilla dentro de una celda y un componente real se montaría y desmontaría
 * en cada render.
 */
function customerCell(row: unknown): unknown {
  const venta = row as SaleRow
  const hijos = [
    h('span', { class: 'block font-medium text-surface-900 dark:text-surface-100' }, venta.customer_name),
  ]

  if (venta.customer_email) {
    hijos.push(
      h('span', { class: 'block text-xs text-surface-500 dark:text-surface-400' }, venta.customer_email),
    )
  }

  return h('div', hijos)
}

const columns = [
  { field: 'sale_number', header: 'Nº Venta' },
  { field: 'sale_date_label', header: 'Fecha' },
  { field: 'customer_name', header: 'Cliente', template: customerCell },
  { field: 'price_type_label', header: 'Tipo', hideOnMobile: true },
  { field: 'user_name_label', header: 'Asesor', hideOnMobile: true },
  { field: 'amount_label', header: 'Valor', style: 'text-align: right' },
  {
    field: 'invoice_state',
    header: 'Facturación',
    severity: (value: string) => value === 'FACTURADA' ? 'success' : 'secondary',
  },
  { field: 'invoice_number_label', header: 'Factura', hideOnMobile: true },
]

const rangeLabel = computed(() => {
  if (total.value === 0) return ''
  const first = (page.value - 1) * perPage + 1
  const last = Math.min(page.value * perPage, total.value)
  return `${first}–${last} de ${total.value}`
})

const hasFilters = computed(() => filtros.alguno)

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message
    ?? 'No se pudieron cargar las ventas de tienda. Revisa tu conexión e inténtalo de nuevo.'
}

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await storeSalesApi.list({
      search: search.value.trim() === '' ? undefined : search.value.trim(),
      date_from: dateFrom.value === '' ? undefined : dateFrom.value,
      date_to: dateTo.value === '' ? undefined : dateTo.value,
      price_type: priceType.value === '' ? undefined : priceType.value as StoreSalePriceType,
      is_invoiced: invoiced.value === '' ? undefined : invoiced.value === '1',
      user_id: advisorId.value === '' || advisorId.value === null ? undefined : Number(advisorId.value),
      page: page.value,
      per_page: perPage,
    })

    items.value = data.data
    total.value = data.meta.total
  } catch (cause: unknown) {
    items.value = []
    total.value = 0
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

async function loadAdvisors(): Promise<void> {
  try {
    const { data } = await storeSalesApi.advisors()
    advisors.value = Array.isArray(data.data) ? data.data : []
  } catch {
    // Sin la lista, el filtro de asesor se queda vacío pero el listado sigue
    // funcionando: no es un fallo que deba tumbar la pantalla.
    advisors.value = []
  }
}

let debounceHandle: ReturnType<typeof setTimeout> | null = null

watch(search, () => {
  page.value = 1
  if (debounceHandle !== null) clearTimeout(debounceHandle)
  debounceHandle = setTimeout(() => {
    void load()
  }, 300)
})

watch([dateFrom, dateTo, priceType, invoiced, advisorId], () => {
  // Cambiar un filtro vuelve a la primera página. Si ya se estaba en ella, se
  // recarga aquí; si no, el cambio de `page` dispara su propio watcher y una
  // segunda petición solo repetiría la misma consulta.
  if (page.value !== 1) {
    page.value = 1
    return
  }

  void load()
})

watch(page, () => {
  void load()
})

onMounted(async () => {
  await loadAdvisors()
  await load()
})

/** Abre una venta o su edición conservando los filtros del listado. */
function open(venta: StoreSale, action: 'view' | 'edit'): void {
  void router.push(
    filtros.conQuery({
      name: action === 'view' ? 'store-sales.show' : 'store-sales.edit',
      params: { id: String(venta.id) },
    }),
  )
}

const invoiceVisible = ref(false)
const invoiceTarget = ref<StoreSale | null>(null)
const invoiceNumber = ref('')
const invoiceError = ref<string | null>(null)
const savingInvoice = ref(false)

function askInvoice(venta: StoreSale): void {
  invoiceTarget.value = venta
  invoiceNumber.value = venta.invoice_number ?? ''
  invoiceError.value = null
  invoiceVisible.value = true
}

async function confirmInvoice(): Promise<void> {
  if (invoiceTarget.value === null) return

  if (invoiceNumber.value.trim() === '') {
    invoiceError.value = 'Escribe el número de factura para marcarla como facturada.'
    return
  }

  savingInvoice.value = true
  invoiceError.value = null

  try {
    await storeSalesApi.markInvoiced(invoiceTarget.value.id, invoiceNumber.value.trim())
    invoiceVisible.value = false
    invoiceTarget.value = null
    await load()
  } catch (cause: unknown) {
    const maybeAxios = cause as {
      validationErrors?: Record<string, string[]>
      response?: { data?: { error?: { message?: string; details?: { fields?: Record<string, string[]> } } } }
    }
    invoiceError.value = maybeAxios.response?.data?.error?.details?.fields?.invoice_number?.[0]
      ?? maybeAxios.response?.data?.error?.message
      ?? 'No se pudo marcar la venta como facturada.'
  } finally {
    savingInvoice.value = false
  }
}

const deleteVisible = ref(false)
const deleteTarget = ref<StoreSale | null>(null)
const deleteError = ref<string | null>(null)
const deleting = ref(false)

function askDelete(venta: StoreSale): void {
  deleteTarget.value = venta
  deleteError.value = null
  deleteVisible.value = true
}

async function confirmDelete(): Promise<void> {
  if (deleteTarget.value === null) return

  deleting.value = true
  deleteError.value = null

  try {
    const id = deleteTarget.value.id
    await storeSalesApi.remove(id)

    // La fila se va sin recargar. Si la página se queda vacía, se retrocede
    // una: prometer una página que ya no existe es peor que recargar.
    items.value = items.value.filter((venta) => venta.id !== id)
    total.value = Math.max(0, total.value - 1)
    deleteVisible.value = false
    deleteTarget.value = null

    if (items.value.length === 0 && page.value > 1) {
      page.value -= 1
    }
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    deleteError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo borrar la venta.'
  } finally {
    deleting.value = false
  }
}

function handleAction(row: unknown, action: string): void {
  const venta = row as StoreSale

  if (action === 'view' || action === 'edit') {
    open(venta, action)
    return
  }

  if (action === 'invoice') {
    askInvoice(venta)
    return
  }

  if (action === 'delete') {
    askDelete(venta)
  }
}

function onPageChange(event: { page: number }): void {
  page.value = event.page + 1
}

function clearFilters(): void {
  filtros.limpiar()
  page.value = 1
  void load()
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Ventas Tienda
        </h1>
        <p class="mt-1 text-surface-500 dark:text-surface-400">
          Ventas cerradas en la tienda, con su facturación y sus anticipos
        </p>
      </div>

      <Button
        v-if="canManage"
        label="Nueva venta"
        icon="pi pi-plus"
        data-testid="store-sale-create"
        @click="router.push(filtros.conQuery({ name: 'store-sales.create' }))"
      />
    </header>

    <AppCard>
      <template #header>
        <form
          class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6"
          role="search"
          @submit.prevent="load"
        >
          <div class="sm:col-span-2">
            <label
              for="store-sale-search"
              class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
            >
              Buscar
            </label>
            <InputText
              id="store-sale-search"
              v-model="search"
              placeholder="Cliente, documento o factura"
              class="w-full"
            />
          </div>

          <DateField
            v-model="dateFrom"
            input-id="store-sale-date-from"
            label="Desde"
          />
          <DateField
            v-model="dateTo"
            input-id="store-sale-date-to"
            label="Hasta"
            :min="dateFrom"
          />

          <div>
            <label
              for="store-sale-price-type"
              class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
            >
              Tipo
            </label>
            <Select
              id="store-sale-price-type"
              v-model="priceType"
              :options="priceTypeOptions"
              option-label="label"
              option-value="value"
              class="w-full"
            />
          </div>

          <div>
            <label
              for="store-sale-invoiced"
              class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
            >
              Facturación
            </label>
            <Select
              id="store-sale-invoiced"
              v-model="invoiced"
              :options="[
                { label: 'Todas', value: '' },
                { label: 'Facturadas', value: '1' },
                { label: 'Pendientes', value: '0' },
              ]"
              option-label="label"
              option-value="value"
              class="w-full"
            />
          </div>

          <div>
            <label
              for="store-sale-advisor"
              class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
            >
              Asesor
            </label>
            <Select
              id="store-sale-advisor"
              v-model="advisorId"
              :options="advisors"
              option-label="name"
              option-value="id"
              placeholder="Todos"
              show-clear
              class="w-full"
            />
          </div>

          <div class="flex items-end">
            <Button
              v-if="hasFilters"
              label="Quitar filtros"
              icon="pi pi-filter-slash"
              severity="secondary"
              outlined
              type="button"
              class="w-full"
              @click="clearFilters"
            />
          </div>
        </form>
      </template>

      <div
        v-if="errorMessage"
        class="flex flex-col items-center gap-3 py-12 text-center"
        role="alert"
      >
        <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          No se pudieron cargar las ventas
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{ errorMessage }}
        </p>
        <Button
          label="Reintentar"
          icon="pi pi-refresh"
          severity="secondary"
          @click="load"
        />
      </div>

      <div
        v-else-if="!loading && items.length === 0"
        class="flex flex-col items-center gap-3 py-12 text-center"
      >
        <i class="pi pi-shopping-bag text-4xl text-surface-300 dark:text-surface-600" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          {{ hasFilters ? 'No hay ventas con esos filtros' : 'Todavía no hay ventas de tienda' }}
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{ hasFilters
            ? 'Prueba con otro cliente, otro rango de fechas o quita los filtros.'
            : 'Cuando se cierre la primera venta en la tienda aparecerá aquí.' }}
        </p>
        <Button
          v-if="hasFilters"
          label="Quitar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          @click="clearFilters"
        />
      </div>

      <template v-else>
        <DenseTable
          v-if="isDesktop"
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :paginator="false"
          layout="fixed"
          @action="handleAction"
          @row-click="(row) => open(row as StoreSale, 'view')"
        />

        <!--
          Tarjetas por debajo de 1024 px. Llevan las mismas acciones que la
          tabla: si falta una, esa acción no existe en móvil.
        -->
        <ul
          v-else
          class="divide-y divide-surface-100 dark:divide-surface-800"
          data-testid="store-sales-cards"
        >
          <li
            v-for="venta in rows"
            :key="venta.id"
            class="py-4 first:pt-0 last:pb-0"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <button
                  type="button"
                  class="text-left font-mono text-sm font-semibold text-primary-700 hover:underline dark:text-primary-300"
                  @click="open(venta, 'view')"
                >
                  {{ venta.sale_number }}
                </button>
                <p class="mt-0.5 text-sm text-surface-500 dark:text-surface-400">
                  {{ venta.sale_date_label }} · {{ venta.price_type_label }}
                </p>
              </div>

              <Tag
                :value="venta.invoice_state"
                :severity="venta.is_invoiced ? 'success' : 'secondary'"
                class="shrink-0"
              />
            </div>

            <div class="mt-3 space-y-1 text-sm">
              <p class="font-medium text-surface-900 dark:text-surface-100">
                {{ venta.customer_name }}
              </p>
              <p
                v-if="venta.customer_email"
                class="truncate text-surface-500 dark:text-surface-400"
              >
                {{ venta.customer_email }}
              </p>
              <p class="text-surface-500 dark:text-surface-400">
                Asesor: {{ venta.user_name_label }}
                <span v-if="venta.invoice_number"> · Factura: {{ venta.invoice_number }}</span>
              </p>
            </div>

            <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p class="text-lg font-semibold tabular-nums text-surface-900 dark:text-surface-100">
                {{ venta.amount_label }}
              </p>

              <div class="flex flex-wrap gap-1">
                <Button
                  label="Ver"
                  icon="pi pi-eye"
                  size="small"
                  severity="secondary"
                  outlined
                  @click="open(venta, 'view')"
                />
                <Button
                  v-if="canManage"
                  label="Editar"
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  @click="open(venta, 'edit')"
                />
                <Button
                  v-if="canManage && !venta.is_invoiced"
                  label="Facturar"
                  icon="pi pi-check-circle"
                  size="small"
                  outlined
                  @click="askInvoice(venta)"
                />
                <Button
                  v-if="canManage"
                  label="Borrar"
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  @click="askDelete(venta)"
                />
              </div>
            </div>
          </li>
        </ul>

        <div
          v-if="total > 0"
          class="mt-4 flex flex-col items-center gap-3 border-t border-surface-200 pt-4 pb-safe sm:flex-row sm:justify-between dark:border-surface-700"
        >
          <p class="text-sm text-surface-500 dark:text-surface-400">
            {{ rangeLabel }}
          </p>
          <Paginator
            :first="(page - 1) * perPage"
            :rows="perPage"
            :total-records="total"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            @page="onPageChange"
          />
        </div>
      </template>
    </AppCard>

    <AppDialog
      v-model:visible="invoiceVisible"
      title="Marcar como facturada"
      confirm-label="Facturar"
      fullscreen-mobile
      @cancel="invoiceTarget = null"
    >
      <div class="space-y-4">
        <p class="text-sm text-surface-600 dark:text-surface-300">
          La venta <strong>{{ invoiceTarget?.sale_number }}</strong> de
          <strong>{{ invoiceTarget?.customer_name }}</strong> quedará como facturada.
        </p>

        <div>
          <label
            for="store-sale-invoice-number"
            class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Número de factura
          </label>
          <InputText
            id="store-sale-invoice-number"
            v-model="invoiceNumber"
            class="w-full"
            placeholder="Número de la factura"
            data-testid="store-sale-invoice-number"
          />
        </div>

        <Message
          v-if="invoiceError"
          severity="error"
          :closable="false"
          variant="simple"
          role="alert"
        >
          {{ invoiceError }}
        </Message>
      </div>

      <template #footer>
        <Button
          label="Cancelar"
          severity="secondary"
          @click="invoiceVisible = false"
        />
        <Button
          label="Facturar"
          :loading="savingInvoice"
          data-testid="store-sale-confirm-invoice"
          @click="confirmInvoice"
        />
      </template>
    </AppDialog>

    <AppDialog
      v-model:visible="deleteVisible"
      :title="`Borrar venta de ${deleteTarget?.customer_name ?? ''}`"
      confirm-label="Borrar"
      fullscreen-mobile
      @cancel="deleteTarget = null"
    >
      <div class="space-y-3">
        <p class="text-sm text-surface-600 dark:text-surface-300">
          Se borrará la venta <strong>{{ deleteTarget?.sale_number }}</strong> y sus anticipos.
          Esta acción no se puede deshacer.
        </p>

        <Message
          v-if="deleteError"
          severity="error"
          :closable="false"
          variant="simple"
          role="alert"
        >
          {{ deleteError }}
        </Message>
      </div>

      <template #footer>
        <Button
          label="Cancelar"
          severity="secondary"
          @click="deleteVisible = false"
        />
        <Button
          label="Borrar"
          severity="danger"
          :loading="deleting"
          data-testid="store-sale-confirm-delete"
          @click="confirmDelete"
        />
      </template>
    </AppDialog>
  </div>
</template>
